import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CatalogService, InventorySerial } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-serials',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('catalog.tracking.serials.title')" [description]="t('catalog.tracking.serials.description')">
      <ain-inventory-page-hint pageKey="serials" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('catalog.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelectedDetail()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>

          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('catalog.filter.hide') : t('catalog.filter.show')" severity="secondary" (click)="showFilters.set(!showFilters())"></button>
          </div>

          @if (showFilters()) {
            <div class="filters">
              <label>
                <span>{{ t('catalog.tracking.serialNumber') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.serialNumber" (ngModelChange)="applyFilters()" />
              </label>
              <label>
                <span>{{ t('catalog.tracking.status') }}</span>
                <p-select [options]="statusOptions" [(ngModel)]="filters.status" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" (ngModelChange)="load()" />
              </label>
            </div>
          }

          <p-table
            [value]="rows()"
            dataKey="id"
            [paginator]="true"
            [rows]="15"
            [selection]="selectedRows()"
            (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('catalog.tracking.serialNumber') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.tracking.lotId') }}</th>
                <th>{{ t('catalog.tracking.issuedReference') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.serialNumber }}</button></td>
                <td>{{ row.status }}</td>
                <td>{{ resolveLotLabel(row.lotId) }}</td>
                <td>{{ row.issuedReferenceNumber || '-' }}</td>
              </tr>
            </ng-template>
          </p-table>

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; justify-content: flex-start; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .filters label { display: grid; gap: 6px; }
    .filters span { font-size: 12px; color: #475569; font-weight: 600; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class InventorySerialsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly showFilters = signal(true);
  readonly sourceRows = signal<InventorySerial[]>([]);
  readonly rows = signal<InventorySerial[]>([]);
  readonly selectedRows = signal<InventorySerial[]>([]);
  readonly filters = { serialNumber: '', status: '' };

  readonly statusOptions = [
    { label: '—', value: '' },
    { label: 'Available', value: 'Available' },
    { label: 'Quarantine', value: 'Quarantine' },
    { label: 'Blocked', value: 'Blocked' },
    { label: 'Issued', value: 'Issued' },
    { label: 'Consumed', value: 'Consumed' },
    { label: 'Scrapped', value: 'Scrapped' }
  ];

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.service.listInventorySerials(undefined, undefined, this.filters.status || undefined).subscribe({
      next: serials => {
        this.sourceRows.set(serials);
        this.applyFilters();
        this.loading.set(false);
      },
      error: () => {
        this.sourceRows.set([]);
        this.rows.set([]);
        this.error.set(this.t('catalog.tracking.serials.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  applyFilters(): void {
    const serialNumber = this.filters.serialNumber.trim().toLowerCase();
    this.rows.set(this.sourceRows().filter(row =>
      (!serialNumber || row.serialNumber.toLowerCase().includes(serialNumber))));
  }

  onSelectionChange(rows: InventorySerial[]): void {
    this.selectedRows.set(rows);
  }

  openCreate(): void {
    void this.router.navigate(['/inventory/serials', 'new']);
  }

  openSelectedDetail(): void {
    const selected = this.selectedRows()[0];
    if (selected) void this.router.navigate(['/inventory/serials', selected.id]);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/inventory/serials', id]);
  }

  resolveLotLabel(lotId: string | null): string {
    if (!lotId) return '-';
    return this.t('catalog.tracking.linkedLot');
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
